/**
 * Full sass, and the fix always present — the reversed rule from PERSONA.md.
 * Every line here is written to stand alone: most of these surfaces show
 * nothing else beside the line itself, so the useful part cannot be left to
 * a hint paragraph that may or may not be there. Where a call site *does*
 * also render its own retry button or hint, the fix repeating itself once is
 * a smaller cost than the fix going missing on the site that has nothing else.
 *
 * These pools also used to be the one place she switched to `jūs` —
 * „Įjunkite", „Patikrinkite", and in one line both registers inside a single
 * sentence. She lives in the flat. A resident does not go formal to tell you
 * the camera is off, and the sudden distance made the app's worst moments read
 * as though someone else had written them. Every imperative here is `tu`.
 */

import type { SassContext } from '@gramas/shared'

type Line = (ctx: SassContext) => string

export const ERRORS: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'error_camera_denied.base': [
    () => 'Kamera atsisakė bendrauti — leidimo nedavei. Įjunk jį naršyklės nustatymuose.',
    () => 'Be leidimo aš nematau nieko. Įjunk kamerą naršyklės nustatymuose ir grįžk.',
    () => 'Kamera užsidarė nuo tavęs. Atrakink ją naršyklės nustatymuose.',
    () => 'Leidimo nėra, vaizdo irgi. Naršyklės nustatymuose jis grąžinamas vienu palietimu.',
    () => 'Na, uždraudei ir stebiesi. Leidimas kamerai — naršyklės nustatymuose.',
  ],
  'error_camera.base': [
    () => 'Kamera nutilo be paaiškinimų. Bandyk dar kartą.',
    () => 'Vaizdas dingo. Būna. Paspausk dar kartą.',
    () => 'Kamera pasitraukė iš pareigų. Grąžink ją mygtuku.',
    () => 'Ryšio su kamera nėra. Antras bandymas dažniausiai padeda.',
    () => 'Kažkas nutrūko. Ne tu kaltas — bandyk iš naujo.',
  ],
  'error_mic_denied.base': [
    () => 'Mikrofono neišgirdau — leidimo nedavei. Įjunk jį naršyklės nustatymuose.',
    () => 'Balsas be leidimo niekur nekeliauja. Atrakink mikrofoną nustatymuose.',
    () => 'Tylu, nes neleidai klausytis. Pataisyk tai naršyklės nustatymuose.',
    () => 'Mikrofonas užrakintas. Raktas — naršyklės nustatymuose.',
    () => 'Aš girdžiu daug ką, bet ne be leidimo. Įjunk jį nustatymuose.',
  ],
  'error_mic.base': [
    () => 'Nieko negirdėjau. Prisiartink ir bandyk dar kartą.',
    () => 'Atpažinimas nutrūko. Kalbėk aiškiau ir bandyk iš naujo.',
    () => 'Tyla mikrofone. Bandyk dar kartą, garsiau.',
    () => 'Negirdėjau nė žodžio. Antras bandymas dažniausiai sėkmingesnis.',
    () => 'Kažką sakei, bet ne man. Pakartok arčiau.',
  ],
  'error_voice_empty.base': [
    () => 'Nieko naudingo neišgirdau. Bandyk dar kartą, aiškiau.',
    () => 'Balsas buvo, maistas — ne. Pabandyk paprastesnį sakinį.',
    () => 'Neatpažinau nė vieno produkto. Vardink juos po vieną.',
    () => 'Tai, ką pasakei, manęs nepasiekė kaip maistas. Bandom dar kartą?',
    () => 'Girdėjau žodžius, bet nė vieno valgomo. Pakartok paprasčiau.',
  ],
  'error_voice_network.base': [
    () => 'Nepavyko susisiekti su serveriu. Patikrink ryšį ir bandyk dar kartą.',
    () => 'Ryšys nutrūko pusiaukelėje. Patikrink tinklą, tada bandyk iš naujo.',
    () => 'Serveris tylėjo. Dažniausiai kaltas jis, ne tu. Bandyk vėl.',
    () => 'Signalas dingo. Patikrink ryšį ir pabandyk dar kartą.',
    () => 'Žodžiai iškeliavo ir nepasiekė adresato. Patikrink ryšį.',
  ],
  'error_offline.base': [
    () => 'Neprisijungta. Paliesk, kai ryšys grįš.',
    () => 'Ryšio nėra. Duomenys palauks — paliesk, kai jis sugrįš.',
    () => 'Tinklas dingęs. Paliesk, kad pabandytum iš naujo.',
    () => 'Neprisijungta prie nieko. Paliesk, kai tai pasikeis.',
    () => 'Interneto nėra. Aš vis tiek čia, tik tylesnė. Paliesk, kai grįš.',
  ],
  'error_sync.base': [
    () => 'Sinchronizavimas nepavyko. Paliesk, kad bandytum dar kartą.',
    () => 'Duomenys užstrigo pusiaukelėje. Paliesk, kad pastumtum juos toliau.',
    () => 'Serveris atsisakė. Paliesk, kad įtikintum jį dar kartą.',
    () => 'Sinchronizacija nutrūko. Paliesk šią juostą — bus dar vienas bandymas.',
    () => 'Kažkas nenuėjo iki galo. Paliesk, pabandysim vėl.',
  ],
  'error_save.base': [
    () => 'Neišsaugota — reikia ryšio su serveriu. Bandyk dar kartą.',
    () => 'Pakeitimai liko šiapus. Reikia ryšio su serveriu, kad keliautų toliau.',
    () => 'Serveris nepriėmė. Patikrink ryšį ir bandyk dar kartą.',
    () => 'Neišsaugota. Be ryšio pakeitimas niekur nedingsta, bet ir neįvyksta.',
    () => 'Bandymas neįskaitytas. Reikia ryšio su serveriu.',
    () => 'Neišsaugota — kažkas sutrukdė. Bandyk dar kartą.',
    () => 'Na po velnių. Neišsaugota. Patikrink ryšį ir spausk dar kartą.',
  ],
  'error_scan.base': [
    () => 'Nepavyko nuskaityti etiketės. Bandyk dar kartą, arčiau ir ryškiau.',
    () => 'Etiketė liko neperskaityta. Bandyk dar kartą.',
    () => 'Vaizdas neišsiskaitė. Antras bandymas dažniausiai aiškesnis.',
    () => 'Nepavyko. Etiketė nekalta — dažniausiai kaltas apšvietimas. Bandyk vėl.',
    () => 'Raidės per mažos net man. Prilaikyk arčiau ir bandyk dar kartą.',
  ],
  'error_login_password.base': [
    () => 'Slaptažodis arba paštas neteisingi. Patikrink ir bandyk dar kartą.',
    () => 'Netinka. Nei paštas, nei slaptažodis manęs neįtikino.',
    () => 'Neatpažinau. Patikrink raides ir bandyk dar kartą.',
    () => 'Durys neatsivėrė. Patikrink slaptažodį.',
    () => 'Ne tas raktas. Pabandyk dar kartą, atidžiau.',
  ],
  'error_login.base': [
    () => 'Nepavyko prisijungti. Patikrink ryšį ir bandyk dar kartą.',
    () => 'Serveris neatsiliepė. Patikrink ryšį, tada bandyk iš naujo.',
    () => 'Prisijungimas nepavyko — greičiausiai ryšys, ne tu. Bandyk dar kartą.',
    () => 'Nepavyko susisiekti. Patikrink tinklą ir pabandyk dar kartą.',
    () => 'Durys užstrigo, ne užsirakino. Bandyk dar kartą.',
  ],
  'error_import.base': [
    () => 'Šis puslapis nieko neatidavė. Patikrink nuorodą arba bandyk kitą.',
    () => 'Recepto ten neradau. Bandyk kitą nuorodą.',
    () => 'Puslapis nepasidalijo ingredientais. Suvesk juos ranka arba bandyk kitą nuorodą.',
    () => 'Nepavyko perskaityti. Nuoroda tikra? Bandyk dar kartą.',
    () => 'Ingredientų sąrašo neradau. Gali būti, kad jo ten tiesiog nėra — bandyk kitą.',
  ],
}

export const ERRORS_META: Readonly<Record<string, number>> = {
  'error_camera_denied.base': 2,
  'error_camera.base': 2,
  'error_mic_denied.base': 2,
  'error_mic.base': 2,
  'error_voice_empty.base': 2,
  'error_voice_network.base': 2,
  'error_offline.base': 2,
  'error_sync.base': 2,
  'error_save.base': 3,
  'error_scan.base': 2,
  'error_login_password.base': 2,
  'error_login.base': 2,
  'error_import.base': 3,
}
